import { useState } from "react";
import { X } from "lucide-react";
import {
  REVIEW_DECISIONS,
  type ReviewDecision,
} from "@/lib/feedback/review-decision";

export interface MarkReviewedDialogProps {
  busy: boolean;
  /** Set when changing an existing decision; the dialog starts on it. */
  initialDecision?: string | null;
  onCancel: () => void;
  onSubmit: (decision: ReviewDecision, note: string) => void;
}

/** Asks what we'll do with a feedback item before it is marked reviewed. */
export function MarkReviewedDialog({
  busy,
  initialDecision,
  onCancel,
  onSubmit,
}: MarkReviewedDialogProps) {
  const changing = initialDecision !== undefined;
  const [decision, setDecision] = useState<ReviewDecision | null>(
    REVIEW_DECISIONS.find(d => d.key === initialDecision)?.key ?? null,
  );
  const [note, setNote] = useState("");
  const title = changing ? "Change decision" : "Mark reviewed";
  const action = changing ? "Save decision" : "Mark reviewed";

  return (
    // Rendered outside the list rows (not inside their links). Never
    // preventDefault on clicks here — that stops the radios from selecting.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mark-reviewed-title"
        className="relative w-full max-w-md rounded-2xl border border-fc-border bg-fc-raised p-6 space-y-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="mark-reviewed-title" className="text-[16px] font-semibold text-fc-text">
              {title}
            </h2>
            <p className="text-[12px] text-fc-text-dim mt-0.5">
              What will we do with this feedback?
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onCancel}
            className="w-8 h-8 rounded-lg text-fc-text-dim hover:text-fc-text hover:bg-fc-overlay flex items-center justify-center transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div role="radiogroup" aria-label="Decision" className="space-y-2">
          {REVIEW_DECISIONS.map(d => {
            const selected = decision === d.key;
            return (
              <label
                key={d.key}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 transition-all ${
                  selected
                    ? "border-fc-brand bg-fc-brand/10"
                    : "border-fc-border bg-fc-surface hover:border-fc-text-dim"
                }`}
              >
                <input
                  type="radio"
                  name="review-decision"
                  value={d.key}
                  checked={selected}
                  onChange={() => setDecision(d.key)}
                  className="mt-0.5 accent-emerald-500"
                />
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-fc-text">{d.label}</span>
                  <span className="block text-[11.5px] text-fc-text-dim">{d.hint}</span>
                </span>
              </label>
            );
          })}
        </div>

        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          placeholder="Note (optional) — e.g. why, or which release"
          maxLength={2000}
          className="w-full px-3 py-2 rounded-xl bg-fc-surface border border-fc-border text-[13px] text-fc-text placeholder:text-fc-text-faint focus:outline-none focus:border-fc-brand resize-none"
        />

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 py-2.5 rounded-xl bg-fc-surface border border-fc-border text-[13px] text-fc-text-secondary hover:text-fc-text hover:bg-fc-overlay transition-all disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => decision && onSubmit(decision, note)}
            disabled={busy || !decision}
            className="flex-1 py-2.5 rounded-xl bg-fc-brand text-black text-[13px] font-semibold hover:brightness-105 disabled:opacity-40 transition-all"
          >
            {busy ? "Saving…" : action}
          </button>
        </div>
      </div>
    </div>
  );
}
